import { useEffect, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const OPTIONS = [
  { key: "light", label: "Light", Icon: Sun },
  { key: "dark", label: "Dark", Icon: Moon },
  { key: "system", label: "System", Icon: Monitor },
];

export const PreferencesCard = () => {
  const { pref, setPref } = useTheme();
  const { user, checkAuth } = useAuth();
  const [codes, setCodes] = useState([]);
  const [dial, setDial] = useState("+91");
  const [local, setLocal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta/country-codes")
      .then(({ data }) => setCodes(data.countries || []))
      .catch(() => setCodes([{ iso: "IN", name: "India", dial_code: "+91" }]));
  }, []);

  const savePhone = async () => {
    setBusy(true);
    try {
      await api.put("/auth/phone", { phone: `${dial}${local.replace(/\D/g, "")}` });
      toast.success("Contact number saved — you can now sign in with it");
      setLocal("");
      await checkAuth();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="preferences-card">
      <h2 className="font-heading font-bold text-sm text-[#111827]">Preferences</h2>
      <p className="text-xs text-[#6B7280] mt-0.5">Appearance and the contact number used for one-time codes.</p>

      <div className="mt-4 grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Appearance</p>
          <div className="mt-2 flex gap-2" data-testid="theme-options">
            {OPTIONS.map(({ key, label, Icon }) => (
              <button key={key} type="button" onClick={() => setPref(key)} data-testid={`theme-option-${key}`}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs ${
                  pref === key ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#374151]"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Contact number</p>
          <p className="text-xs text-[#6B7280] mt-1" data-testid="current-phone">
            {user?.phone ? `Currently linked: ${user.phone}` : "No number linked yet"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <select value={dial} onChange={(e) => setDial(e.target.value)} data-testid="pref-country-select"
              className="h-10 w-full sm:w-40 min-w-0 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
              {codes.map((c) => <option key={c.iso} value={c.dial_code}>{`${c.name} (${c.dial_code})`}</option>)}
            </select>
            <Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder="98765 43210"
              inputMode="tel" data-testid="pref-phone-input" className="h-10 flex-1 min-w-[140px]" />
            <Button onClick={savePhone} disabled={busy || !local} data-testid="pref-phone-save"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white h-10">Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
